/**
 * TopCaregiversTable — Top 10 caregivers by alarm volume.
 *
 * Grandstream campuses get an Avg Connect column next to Avg Response
 * (docs/TIME_TO_CONNECT_PLAN.md, phase 3): each caregiver's average Time to Connect over
 * THEIR alerts where the intercom was used (sp_getCaregiverSummary v2), N/A when none.
 */

import { Link } from 'react-router-dom';
import { exportWellnessCsv } from '../../../../services/csvExport.js';
import { dashboardWeekRanges } from '../weekRanges.js';
import HeaderInfoTip from '../../../../components/v2/HeaderInfoTip.jsx';
import { TIME_TO_CONNECT_NA } from '../../../reports/alert-details/timeToConnect.js';

const CAREGIVERS_TOOLTIP = [
    'This table lists the top 10 caregivers who responded to the most calls over the last 28 days.',
    'The Total column is the average daily events handled across that 28-day window,',
    'and each weekly column shows the average daily events for that specific week.',
    'Avg Response is the average time, in minutes, from when an alert was triggered to when the caregiver acknowledged it.',
];

const CONNECT_TOOLTIP = "Avg Connect is the average time, in minutes, from the alert to the caregiver's first "
    + 'intercom call to the resident or their room, over only the alerts where they used the intercom. N/A means '
    + 'they used it on none.';


const CSV_COLS = [
    { key: 'display_name', label: 'Caregiver' },
    { key: 'avg_daily_events', label: 'Total' },
    { key: 'week1', label: 'Week 1' },
    { key: 'week2', label: 'Week 2' },
    { key: 'week3', label: 'Week 3' },
    { key: 'week4', label: 'Week 4' },
    { key: 'avg_response_time', label: 'Avg Response (min)' },
];

/* Blank, not "N/A", in the CSV: a minutes column in a spreadsheet (same rule as the
   report CSVs). exportWellnessCsv reads the key straight off the row. */
const CONNECT_CSV_COL = { key: 'avg_time_to_connect', label: 'Avg Time to Connect (min)' };

const TopCaregiversTable = ({ data, isGrandstream = false }) => {
    const rows = Array.isArray(data) ? data.slice(0, 10) : [];
    // Newest first: [0] = 1..7 days ago ... [3] = 22..28 (weekRanges.js).
    const weekRanges = dashboardWeekRanges();
    const csvCols = isGrandstream ? [...CSV_COLS, CONNECT_CSV_COL] : CSV_COLS;
    // Caregiver + Total + 5 daily-events columns + Avg Response (+ Avg Connect).
    const colCount = isGrandstream ? 9 : 8;

    return (
        <div className="md-section">
            <div className="md-section__header">
                <h3 className="md-section__title">Top 10 Caregiver Review<HeaderInfoTip title="About This Table" body={isGrandstream ? `${CAREGIVERS_TOOLTIP.join(' ')} ${CONNECT_TOOLTIP}` : CAREGIVERS_TOOLTIP.join(' ')} /></h3>
                {rows.length > 0 ? (
                    <button className="rp-btn rp-btn--secondary" onClick={() => exportWellnessCsv(rows, csvCols, 'top-caregivers.csv')} style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}>
                        <i className="fe fe-download" style={{ marginRight: '4px' }} />CSV
                    </button>
                ) : null}
            </div>
            <div className="dp-table-wrap">
                <table className="dp-table">
                    <thead className="dp-thead">
                        <tr>
                            <th rowSpan={2} className="dp-th">Caregiver</th>
                            <th rowSpan={2} className="dp-th dp-th--center">Total Events</th>
                            <th colSpan={5} className="dp-th dp-th--center" style={{ borderBottomColor: 'lightgrey' }}>Average Daily Events</th>
                            <th rowSpan={2} className="dp-th dp-th--center">Avg Response (min)</th>
                            {isGrandstream ? <th rowSpan={2} className="dp-th dp-th--center">Avg Connect (min)</th> : null}
                        </tr>
                        <tr>
                            <th className="dp-th dp-th--center">{weekRanges[3]}</th>
                            <th className="dp-th dp-th--center">{weekRanges[2]}</th>
                            <th className="dp-th dp-th--center">{weekRanges[1]}</th>
                            <th className="dp-th dp-th--center">{weekRanges[0]}</th>
                            <th className="dp-th dp-th--center">Full 28 Days</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr className="dp-tr"><td className="dp-td dp-td--center" colSpan={colCount}><span className="dp-cell-text dp-cell-text--muted">No caregiver data</span></td></tr>
                        ) : null}
                        {rows.map((r, i) => (
                            <tr key={i} className="dp-tr">
                                <td className="dp-td">{r.user_id ? <Link to={`/profiles/user/${r.user_id}`} className="pp-field__value--link">{r.display_name}</Link> : (r.display_name || '—')}</td>
                                <td className="dp-td dp-td--center">{r.total_events ?? '-'}</td>
                                <td className="dp-td dp-td--center">{r.week4 ?? '-'}</td>
                                <td className="dp-td dp-td--center">{r.week3 ?? '-'}</td>
                                <td className="dp-td dp-td--center">{r.week2 ?? '-'}</td>
                                <td className="dp-td dp-td--center">{r.week1 ?? '-'}</td>
                                <td className="dp-td dp-td--center">{r.avg_daily_events ?? '-'}</td>
                                <td className="dp-td dp-td--center">{r.avg_response_time ?? '—'}</td>
                                {isGrandstream ? (
                                    <td className="dp-td dp-td--center">{r.avg_time_to_connect ?? TIME_TO_CONNECT_NA}</td>
                                ) : null}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TopCaregiversTable;
